/**
 * Behavioral Dimensions System
 * 
 * SINGLE SOURCE OF TRUTH for behavioral dimensions across all applications.
 * Universal behavioral dimensions that can be applied to any scenario context:
 * sales, healthcare, interrogation, interviews, customer service, etc.
 * 
 * This file is the canonical source. Other files should import from here.
 */

// Core behavioral dimensions
export type CooperationLevel = 'cooperative' | 'neutral' | 'resistant';
export type EngagementLevel = 'engaged' | 'indifferent' | 'disengaged';
export type CommunicationStyle = 'terse' | 'direct' | 'balanced' | 'diplomatic' | 'assertive' | 'verbose' | 'passive' | 'blunt' | 'aggressive' | 'hostile' | 'rude';
export type EmotionalState = 'calm' | 'stressed' | 'confident' | 'suspicious';
export type PersonalityOrientation = 'detail_oriented' | 'big_picture' | 'analytical' | 'emotional';

export type InitialEmotion = 
  | 'neutral' | 'confident' | 'skeptical' | 'frustrated' | 'worried' | 'interested'
  | 'encouraging' | 'proud' | 'amused' | 'relieved' | 'enthusiastic' | 'attentive'
  | 'thinking' | 'curious' | 'impatient' | 'hesitant' | 'disappointed'
  | 'contemplative' | 'uncertain' | 'doubtful' | 'concerned' | 'pensive'
  | 'unconvinced' | 'resigned';

export interface BehavioralProfile {
  cooperation: CooperationLevel;
  engagement: EngagementLevel;
  communication: CommunicationStyle;
  emotionalState: EmotionalState;
  orientation: PersonalityOrientation;
  initialEmotion?: InitialEmotion;
}

export interface BehaviorTemplate {
  id: string;
  name: string;
  description: string;
  profile: BehavioralProfile;
  usageExamples: string[];
}

// Dimension definitions with descriptions
export const cooperationLevels: Record<CooperationLevel, { label: string; description: string }> = {
  cooperative: {
    label: 'Cooperative',
    description: 'Willing to help, collaborate, and work together'
  },
  neutral: {
    label: 'Neutral',
    description: 'Neither helpful nor resistant, goes with the flow'
  },
  resistant: {
    label: 'Resistant',
    description: 'Reluctant to help, creates obstacles, opposes suggestions'
  }
};

export const engagementLevels: Record<EngagementLevel, { label: string; description: string }> = {
  engaged: {
    label: 'Engaged',
    description: 'Interested, motivated, actively participating'
  },
  indifferent: {
    label: 'Indifferent',
    description: 'Neither interested nor disinterested, neutral attitude'
  },
  disengaged: {
    label: 'Disengaged',
    description: 'Uninterested, distracted, wants to end the interaction'
  }
};

export const communicationStyles: Record<CommunicationStyle, { label: string; description: string }> = {
  // Courteous styles
  terse: {
    label: 'Terse',
    description: 'Brief responses, minimal words, gets straight to the point'
  },
  direct: {
    label: 'Direct',
    description: 'Straight to the point, no-nonsense, factual'
  },
  balanced: {
    label: 'Balanced',
    description: 'Moderate length, professional, clear communication'
  },
  diplomatic: {
    label: 'Diplomatic',
    description: 'Polite, careful with words, considerate'
  },
  assertive: {
    label: 'Assertive',
    description: 'Confident, firm, stands their ground'
  },
  verbose: {
    label: 'Verbose',
    description: 'Elaborate, detailed explanations, comprehensive responses'
  },
  passive: {
    label: 'Passive',
    description: 'Submissive, avoids confrontation, follows others\' lead'
  },
  // Discourteous styles
  blunt: {
    label: 'Blunt',
    description: 'No social filter, says exactly what they think without considering how it lands'
  },
  aggressive: {
    label: 'Aggressive',
    description: 'Confrontational, pushy, uses demands rather than requests'
  },
  hostile: {
    label: 'Hostile',
    description: 'Openly antagonistic, combative, treats others as adversaries'
  },
  rude: {
    label: 'Rude',
    description: 'Deliberately discourteous, dismissive, shows contempt'
  }
};

export const emotionalStates: Record<EmotionalState, { label: string; description: string }> = {
  calm: {
    label: 'Calm',
    description: 'Relaxed, controlled, composed'
  },
  stressed: {
    label: 'Stressed',
    description: 'Anxious, worried, under pressure'
  },
  confident: {
    label: 'Confident',
    description: 'Self-assured, certain, optimistic'
  },
  suspicious: {
    label: 'Suspicious',
    description: 'Distrustful, cautious, questioning motives'
  }
};

export const personalityOrientations: Record<PersonalityOrientation, { label: string; description: string }> = {
  detail_oriented: {
    label: 'Detail-Oriented',
    description: 'Focuses on specifics, wants comprehensive information'
  },
  big_picture: {
    label: 'Big Picture',
    description: 'Focuses on overall concepts, prefers summaries'
  },
  analytical: {
    label: 'Analytical',
    description: 'Logical, rational, data-driven decision making'
  },
  emotional: {
    label: 'Emotional',
    description: 'Intuitive, feelings-driven, values personal connection'
  }
};

export const initialEmotions: Record<InitialEmotion, { label: string; description: string }> = {
  neutral: { label: 'Neutral', description: 'No particular emotion, baseline state' },
  confident: { label: 'Confident', description: 'Self-assured and optimistic' },
  skeptical: { label: 'Skeptical', description: 'Doubtful, questioning validity' },
  frustrated: { label: 'Frustrated', description: 'Annoyed, blocked from goals' },
  worried: { label: 'Worried', description: 'Anxious about outcomes' },
  interested: { label: 'Interested', description: 'Curious and engaged' },
  encouraging: { label: 'Encouraging', description: 'Supportive and motivating' },
  proud: { label: 'Proud', description: 'Satisfied with achievements' },
  amused: { label: 'Amused', description: 'Finding humor in situation' },
  relieved: { label: 'Relieved', description: 'Stress or worry lifted' },
  enthusiastic: { label: 'Enthusiastic', description: 'Excited and eager' },
  attentive: { label: 'Attentive', description: 'Focused and listening carefully' },
  thinking: { label: 'Thinking', description: 'Processing information' },
  curious: { label: 'Curious', description: 'Wanting to learn more' },
  impatient: { label: 'Impatient', description: 'Wanting faster progress' },
  hesitant: { label: 'Hesitant', description: 'Uncertain about proceeding' },
  disappointed: { label: 'Disappointed', description: 'Expectations not met' },
  contemplative: { label: 'Contemplative', description: 'Deep in thought' },
  uncertain: { label: 'Uncertain', description: 'Not sure what to think' },
  doubtful: { label: 'Doubtful', description: 'Skeptical about claims' },
  concerned: { label: 'Concerned', description: 'Worried about something specific' },
  pensive: { label: 'Pensive', description: 'Thoughtfully sad or serious' },
  unconvinced: { label: 'Unconvinced', description: 'Not persuaded by arguments' },
  resigned: { label: 'Resigned', description: 'Accepted an unwanted situation' }
};

// Pre-defined behavior templates
export const behaviorTemplates: BehaviorTemplate[] = [
  {
    id: 'ideal_customer',
    name: 'Ideal Customer',
    description: 'The dream customer - interested, cooperative, and easy to work with',
    profile: {
      cooperation: 'cooperative',
      engagement: 'engaged',
      communication: 'diplomatic',
      emotionalState: 'confident',
      orientation: 'analytical',
      initialEmotion: 'confident'
    },
    usageExamples: ['Sales training', 'Customer service', 'Product demos']
  },
  {
    id: 'difficult_customer',
    name: 'Difficult Customer',
    description: 'Challenging customer who tests your patience and skills',
    profile: {
      cooperation: 'resistant',
      engagement: 'disengaged',
      communication: 'assertive',
      emotionalState: 'suspicious',
      orientation: 'emotional',
      initialEmotion: 'skeptical'
    },
    usageExamples: ['Sales objection handling', 'Complaint resolution', 'Conflict management']
  },
  {
    id: 'skeptical_buyer',
    name: 'Skeptical Buyer',
    description: 'Interested but cautious, needs convincing with facts and proof',
    profile: {
      cooperation: 'neutral',
      engagement: 'engaged',
      communication: 'direct',
      emotionalState: 'suspicious',
      orientation: 'analytical',
      initialEmotion: 'skeptical'
    },
    usageExamples: ['B2B sales', 'High-value purchases', 'Technical products']
  },
  {
    id: 'busy_executive',
    name: 'Busy Executive',
    description: 'Time-pressed decision maker who wants quick, high-level information',
    profile: {
      cooperation: 'neutral',
      engagement: 'indifferent',
      communication: 'direct',
      emotionalState: 'confident',
      orientation: 'big_picture',
      initialEmotion: 'confident'
    },
    usageExamples: ['Executive presentations', 'Quick sales pitches', 'Status updates']
  },
  {
    id: 'anxious_person',
    name: 'Anxious Person',
    description: 'Someone who is worried, needs reassurance and detailed information',
    profile: {
      cooperation: 'cooperative',
      engagement: 'engaged',
      communication: 'passive',
      emotionalState: 'stressed',
      orientation: 'detail_oriented',
      initialEmotion: 'worried'
    },
    usageExamples: ['Healthcare consultations', 'Insurance sales', 'Financial planning']
  },
  {
    id: 'hostile_person',
    name: 'Hostile Person',
    description: 'Angry or aggressive individual who needs de-escalation',
    profile: {
      cooperation: 'resistant',
      engagement: 'engaged',
      communication: 'aggressive',
      emotionalState: 'stressed',
      orientation: 'emotional',
      initialEmotion: 'frustrated'
    },
    usageExamples: ['Conflict resolution', 'Complaint handling', 'Crisis management']
  }
];

// Utility functions
export function getBehaviorDescription(profile: BehavioralProfile): string {
  const parts: string[] = [];
  
  if (profile.cooperation && cooperationLevels[profile.cooperation]) {
    parts.push(cooperationLevels[profile.cooperation].label.toLowerCase());
  }
  if (profile.engagement && engagementLevels[profile.engagement]) {
    parts.push(engagementLevels[profile.engagement].label.toLowerCase());
  }
  if (profile.communication && communicationStyles[profile.communication]) {
    parts.push(communicationStyles[profile.communication].label.toLowerCase());
  }
  if (profile.emotionalState && emotionalStates[profile.emotionalState]) {
    parts.push(emotionalStates[profile.emotionalState].label.toLowerCase());
  }
  if (profile.orientation && personalityOrientations[profile.orientation]) {
    parts.push(personalityOrientations[profile.orientation].label.toLowerCase());
  }
  
  if (parts.length === 0) {
    return 'No behavioral profile specified';
  }
  
  return `This person is ${parts.join(', ')}.`;
}

export function findTemplateById(id: string): BehaviorTemplate | undefined {
  return behaviorTemplates.find(template => template.id === id);
}

export function createCustomProfile(
  cooperation: CooperationLevel,
  engagement: EngagementLevel,
  communication: CommunicationStyle,
  emotionalState: EmotionalState,
  orientation: PersonalityOrientation
): BehavioralProfile {
  return { cooperation, engagement, communication, emotionalState, orientation };
}

export function generateBehavioralPrompt(profile: BehavioralProfile, name?: string): string {
  const personRef = name ? name : "This person";
  const traits: string[] = [];
  
  switch (profile.cooperation) {
    case 'cooperative': traits.push('helpful and collaborative'); break;
    case 'neutral': traits.push('neither particularly helpful nor resistant'); break;
    case 'resistant': traits.push('reluctant to cooperate and may create obstacles'); break;
  }
  
  switch (profile.engagement) {
    case 'engaged': traits.push('interested and motivated to participate'); break;
    case 'indifferent': traits.push('shows little interest either way'); break;
    case 'disengaged': traits.push('uninterested and wanting to end the interaction quickly'); break;
  }
  
  switch (profile.communication) {
    case 'terse': traits.push('communicates with brief, minimal responses'); break;
    case 'direct': traits.push('communicates in a straightforward, no-nonsense manner'); break;
    case 'balanced': traits.push('communicates with professional, measured responses'); break;
    case 'diplomatic': traits.push('speaks politely and carefully considers their words'); break;
    case 'assertive': traits.push('speaks confidently and stands their ground'); break;
    case 'verbose': traits.push('provides elaborate, detailed explanations'); break;
    case 'passive': traits.push('speaks quietly and avoids confrontation'); break;
    case 'blunt': traits.push('speaks without social filter, saying exactly what they think'); break;
    case 'aggressive': traits.push('communicates in a confrontational, pushy manner'); break;
    case 'hostile': traits.push('is openly antagonistic and combative'); break;
    case 'rude': traits.push('is deliberately discourteous and dismissive'); break;
  }
  
  switch (profile.emotionalState) {
    case 'calm': traits.push('maintains a relaxed and composed demeanor'); break;
    case 'stressed': traits.push('appears anxious and under pressure'); break;
    case 'confident': traits.push('displays self-assurance and optimism'); break;
    case 'suspicious': traits.push('seems distrustful and questions motives'); break;
  }
  
  switch (profile.orientation) {
    case 'detail_oriented': traits.push('focuses on specifics and wants comprehensive information'); break;
    case 'big_picture': traits.push('prefers high-level concepts and summaries'); break;
    case 'analytical': traits.push('makes logical, data-driven decisions'); break;
    case 'emotional': traits.push('relies on intuition and values personal connections'); break;
  }
  
  return `${personRef} is ${traits.join(', ')}.`;
}
